import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Car, Search } from "lucide-react";

const YEARS = Array.from({ length: 30 }, (_, i) => String(2025 - i));

export default function VehicleSelector({ variant = "hero" }) {
  const navigate = useNavigate();
  const [makes,  setMakes]  = useState([]);
  const [models, setModels] = useState([]);
  const [make,   setMake]   = useState("");
  const [model,  setModel]  = useState("");
  const [year,   setYear]   = useState("");
  const [loadingMakes, setLoadingMakes] = useState(true);

  useEffect(() => {
    api.get("/vehicles/makes")
      .then((r) => setMakes(r.data.makes || []))
      .catch(() => setMakes([]))
      .finally(() => setLoadingMakes(false));
  }, []);

  useEffect(() => {
    if (!make) { setModels([]); setModel(""); return; }
    api.get(`/vehicles/${encodeURIComponent(make)}/models`)
      .then((r) => setModels(r.data.models || []))
      .catch(() => setModels([]));
    setModel("");
  }, [make]);

  const submit = () => {
    const p = new URLSearchParams();
    if (make)  p.set("vehicle_make",  make);
    if (model) p.set("vehicle_model", model);
    if (year)  p.set("vehicle_year",  year);
    navigate(`/shop?${p.toString()}`);
  };

  const selectClass = "w-full h-12 px-3 border border-[#1e5bb8]/30 rounded-sm bg-white text-[#0b1a3b] text-sm font-medium focus:outline-none focus:border-[#1e5bb8] focus:ring-1 focus:ring-[#1e5bb8] appearance-none cursor-pointer";
  const selectStyle = { backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%231e5bb8' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")", backgroundPosition: "right 8px center", backgroundRepeat: "no-repeat", backgroundSize: "20px" };

  return (
    <div className="bcp-gradient-navy relative z-20 w-full max-w-6xl mx-auto rounded-sm shadow-2xl" data-testid="vehicle-selector">

      {/* Header */}
      <div className="px-6 py-3.5 border-b border-white/10 flex items-center gap-3">
        <Car className="h-4 w-4 text-[#e31e24] shrink-0" />
        <span className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-white">
          Find Parts for Your Vehicle
        </span>
        <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.3em] text-blue-300">
          Step 1 · 2 · 3
        </span>
      </div>

      {/* Selects */}
      <div className="p-5 grid grid-cols-1 md:grid-cols-4 gap-3">

        {/* Make */}
        <div className="relative">
          {makes.length > 0 || !loadingMakes ? (
            <select value={make} onChange={(e) => setMake(e.target.value)}
              className={selectClass} style={selectStyle}
              data-testid="vehicle-make-select">
              <option value="">Make</option>
              {makes.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          ) : (
            <div className={`${selectClass} flex items-center text-zinc-400`}>Loading makes…</div>
          )}
        </div>

        {/* Model */}
        <div className="relative">
          <select value={model} onChange={(e) => setModel(e.target.value)}
            disabled={!make || models.length === 0}
            className={`${selectClass} ${!make || models.length === 0 ? "opacity-60 cursor-not-allowed" : ""}`}
            style={selectStyle}
            data-testid="vehicle-model-select">
            <option value="">{make && models.length === 0 ? "No models found" : make ? "Model" : "Select make first"}</option>
            {models.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>

        {/* Year */}
        <div className="relative">
          <select value={year} onChange={(e) => setYear(e.target.value)}
            className={selectClass} style={selectStyle}
            data-testid="vehicle-year-select">
            <option value="">Year</option>
            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>

        {/* Find Parts button */}
        <button onClick={submit}
          className="h-12 px-6 bg-[#e31e24] hover:bg-[#b71820] text-white font-heading font-bold uppercase tracking-widest text-sm transition-colors flex items-center justify-center gap-2 rounded-sm"
          data-testid="vehicle-find-parts-btn">
          <Search className="h-4 w-4" /> Find Parts
        </button>
      </div>

      {/* Helper text when no inventory data */}
      {!loadingMakes && makes.length === 0 && (
        <div className="px-6 pb-4 text-blue-300 text-xs font-mono text-center">
          No vehicle data yet — search by part name above or import inventory via CSV
        </div>
      )}
    </div>
  );
}